// IndexedDB storage for Slack & Teams messaging data
//
// Uses a dedicated database ("verigit-messaging", v1) to avoid bumping the
// main verigit DB version for every messaging schema change.

import { openDB, type IDBPDatabase } from 'idb';
import type {
  SlackSettings,
  TeamsSettings,
  MessagingMessage,
  MessagingSnapshot,
  BotAction,
} from './messaging-types';

const DB_NAME = 'verigit-messaging';
const DB_VERSION = 2;

const STORES = {
  SETTINGS: 'settings',
  MESSAGES: 'messages',
  SNAPSHOTS: 'snapshots',
  CURSORS: 'cursors',
  BOT_QUEUE: 'bot_queue',
  ALERTED_ANOMALIES: 'alerted_anomalies',
} as const;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
          db.createObjectStore(STORES.SETTINGS);
        }
        if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
          const ms = db.createObjectStore(STORES.MESSAGES, { keyPath: ['platform', 'channelId', 'id'] });
          ms.createIndex('by_channel', ['platform', 'channelId']);
          ms.createIndex('by_timestamp', 'timestamp');
        }
        if (!db.objectStoreNames.contains(STORES.SNAPSHOTS)) {
          const ss = db.createObjectStore(STORES.SNAPSHOTS, { keyPath: ['platform', 'date'] });
          ss.createIndex('by_platform', 'platform');
        }
        if (!db.objectStoreNames.contains(STORES.CURSORS)) {
          db.createObjectStore(STORES.CURSORS);
        }
        if (!db.objectStoreNames.contains(STORES.BOT_QUEUE)) {
          const bq = db.createObjectStore(STORES.BOT_QUEUE, { keyPath: 'id' });
          bq.createIndex('by_platform', 'platform');
          bq.createIndex('pending', 'sentAt');
        }
        if (!db.objectStoreNames.contains(STORES.ALERTED_ANOMALIES)) {
          db.createObjectStore(STORES.ALERTED_ANOMALIES);
        }
      },
    });
  }
  return dbPromise;
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export async function getSlackSettings(): Promise<SlackSettings | null> {
  const db = await getDB();
  return (await db.get(STORES.SETTINGS, 'slack')) ?? null;
}

export async function saveSlackSettings(settings: SlackSettings | null): Promise<void> {
  const db = await getDB();
  if (settings === null) {
    await db.delete(STORES.SETTINGS, 'slack');
  } else {
    await db.put(STORES.SETTINGS, settings, 'slack');
  }
}

export async function getTeamsSettings(): Promise<TeamsSettings | null> {
  const db = await getDB();
  return (await db.get(STORES.SETTINGS, 'teams')) ?? null;
}

export async function saveTeamsSettings(settings: TeamsSettings | null): Promise<void> {
  const db = await getDB();
  if (settings === null) {
    await db.delete(STORES.SETTINGS, 'teams');
  } else {
    await db.put(STORES.SETTINGS, settings, 'teams');
  }
}

// ─── Messages ─────────────────────────────────────────────────────────────────

/** Persist a batch of new messages (upsert). */
export async function saveMessages(messages: MessagingMessage[]): Promise<void> {
  if (messages.length === 0) return;
  const db = await getDB();
  const tx = db.transaction(STORES.MESSAGES, 'readwrite');
  await Promise.all([
    ...messages.map(m => tx.store.put(m)),
    tx.done,
  ]);
}

/** Retrieve messages for a channel within an optional time range. */
export async function getMessages(
  platform: 'slack' | 'teams',
  channelId: string,
  since?: string,
  until?: string,
): Promise<MessagingMessage[]> {
  const db = await getDB();
  const all: MessagingMessage[] = await db.getAllFromIndex(
    STORES.MESSAGES,
    'by_channel',
    [platform, channelId],
  );
  return all.filter(m => {
    if (since && m.timestamp < since) return false;
    if (until && m.timestamp > until) return false;
    return true;
  });
}

/** Get all messages for a platform in a given day (ISO date: YYYY-MM-DD). */
export async function getMessagesByDay(
  platform: 'slack' | 'teams',
  date: string,
): Promise<MessagingMessage[]> {
  const db = await getDB();
  const range = IDBKeyRange.bound(`${date}T00:00:00.000Z`, `${date}T23:59:59.999Z`);
  const all: MessagingMessage[] = await db.getAllFromIndex(STORES.MESSAGES, 'by_timestamp', range);
  return all.filter(m => m.platform === platform);
}

/** Get all messages for a platform within a date range (inclusive). */
export async function getMessagesByDateRange(
  platform: 'slack' | 'teams',
  fromDate: string,
  toDate: string,
): Promise<MessagingMessage[]> {
  const db = await getDB();
  const range = IDBKeyRange.bound(`${fromDate}T00:00:00.000Z`, `${toDate}T23:59:59.999Z`);
  const all: MessagingMessage[] = await db.getAllFromIndex(STORES.MESSAGES, 'by_timestamp', range);
  return all.filter(m => m.platform === platform);
}

// ─── Cursors ─────────────────────────────────────────────────────────────────

/** Get the last-seen message cursor for a channel (Slack ts or Teams ISO ts). */
export async function getCursor(platform: string, channelId: string): Promise<string | undefined> {
  const db = await getDB();
  return db.get(STORES.CURSORS, `${platform}:${channelId}`);
}

/** Persist all channel cursors for a platform at once. */
export async function saveCursors(
  platform: string,
  cursors: Map<string, string>,
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(STORES.CURSORS, 'readwrite');
  for (const [channelId, cursor] of cursors) {
    tx.store.put(cursor, `${platform}:${channelId}`);
  }
  await tx.done;
}

/** Delete all cursors for a platform so the next poll fetches from scratch. */
export async function clearCursors(platform: string): Promise<void> {
  const db = await getDB();
  const keys = await db.getAllKeys(STORES.CURSORS) as string[];
  const tx = db.transaction(STORES.CURSORS, 'readwrite');
  for (const key of keys) {
    if (key.startsWith(`${platform}:`)) tx.store.delete(key);
  }
  await tx.done;
}

/** Load all cursors for a platform into a Map. */
export async function loadCursors(platform: string): Promise<Map<string, string>> {
  const db = await getDB();
  const keys = await db.getAllKeys(STORES.CURSORS) as string[];
  const map = new Map<string, string>();
  for (const key of keys) {
    if (!key.startsWith(`${platform}:`)) continue;
    const channelId = key.slice(platform.length + 1);
    const value = await db.get(STORES.CURSORS, key) as string | undefined;
    if (value) map.set(channelId, value);
  }
  return map;
}

// ─── Snapshots ────────────────────────────────────────────────────────────────

export async function saveSnapshot(snapshot: MessagingSnapshot): Promise<void> {
  const db = await getDB();
  await db.put(STORES.SNAPSHOTS, snapshot);
}

export async function getSnapshot(
  platform: 'slack' | 'teams',
  date: string,
): Promise<MessagingSnapshot | undefined> {
  const db = await getDB();
  return db.get(STORES.SNAPSHOTS, [platform, date]);
}

/** Get the N most recent snapshots for a platform. */
export async function getRecentSnapshots(
  platform: 'slack' | 'teams',
  limit = 7,
): Promise<MessagingSnapshot[]> {
  const db = await getDB();
  const all: MessagingSnapshot[] = await db.getAllFromIndex(STORES.SNAPSHOTS, 'by_platform', platform);
  return all.sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit);
}

// ─── Bot Queue ────────────────────────────────────────────────────────────────

export async function enqueueBotAction(action: BotAction): Promise<void> {
  const db = await getDB();
  await db.put(STORES.BOT_QUEUE, action);
}

export async function getPendingBotActions(platform: 'slack' | 'teams'): Promise<BotAction[]> {
  const db = await getDB();
  const all: BotAction[] = await db.getAllFromIndex(STORES.BOT_QUEUE, 'by_platform', platform);
  return all.filter(a => !a.sentAt);
}

export async function markBotActionSent(id: string): Promise<void> {
  const db = await getDB();
  const action: BotAction | undefined = await db.get(STORES.BOT_QUEUE, id);
  if (!action) return;
  await db.put(STORES.BOT_QUEUE, { ...action, sentAt: new Date().toISOString() });
}

export async function markBotActionFailed(id: string, error: string): Promise<void> {
  const db = await getDB();
  const action: BotAction | undefined = await db.get(STORES.BOT_QUEUE, id);
  if (!action) return;
  await db.put(STORES.BOT_QUEUE, { ...action, error });
}

// ─── Alerted Anomalies ────────────────────────────────────────────────────────

interface AlertedAnomalyEntry { key: string; severity: string; date: string }

/**
 * Load today's alerted anomaly map from IndexedDB.
 * Entries from previous days are automatically purged.
 */
export async function loadAlertedAnomalies(today: string): Promise<Map<string, string>> {
  const db = await getDB();
  const all: AlertedAnomalyEntry[] = await db.getAll(STORES.ALERTED_ANOMALIES);
  const result = new Map<string, string>();
  const tx = db.transaction(STORES.ALERTED_ANOMALIES, 'readwrite');
  for (const entry of all) {
    if (entry.date !== today) {
      tx.store.delete(entry.key);
    } else {
      result.set(entry.key, entry.severity);
    }
  }
  await tx.done;
  return result;
}

/** Upsert a single alerted anomaly entry. */
export async function saveAlertedAnomaly(key: string, severity: string, today: string): Promise<void> {
  const db = await getDB();
  await db.put(STORES.ALERTED_ANOMALIES, { key, severity, date: today } satisfies AlertedAnomalyEntry, key);
}
